/**
 * Date formatting utilities for the SMS web app
 */

/**
 * Format a date string or Date object to "25 July, 2025" format
 * @param {string|Date} date - The date to format
 * @returns {string} - Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return '';
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';
    const day = dateObj.getDate();
    const mon = dateObj.toLocaleString('en-US', { month: 'short' }); // Jan, Feb, ...
    const year = dateObj.getFullYear();
    return `${day} ${mon}, ${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Format a date string or Date object to "25 July, 2025 at 2:30 PM" format
 * @param {string|Date} date - The date to format
 * @returns {string} - Formatted date and time string
 */
export const formatDateTime = (date) => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    const options = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    
    return dateObj.toLocaleDateString('en-US', options);
  } catch (error) {
    console.error('Error formatting date and time:', error);
    return '';
  }
};

/**
 * Format a date string or Date object to "July 2025" format
 * @param {string|Date} date - The date to format
 * @returns {string} - Formatted month and year string
 */
export const formatMonthYear = (date) => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    const options = {
      month: 'long',
      year: 'numeric'
    };
    
    return dateObj.toLocaleDateString('en-US', options);
  } catch (error) {
    console.error('Error formatting month and year:', error);
    return '';
  }
};

/**
 * Format a date string or Date object to "25/07/2025" format
 * @param {string|Date} date - The date to format
 * @returns {string} - Formatted date string in DD/MM/YYYY format
 */
export const formatDateShort = (date) => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    const day = dateObj.getDate().toString().padStart(2, '0');
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const year = dateObj.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Error formatting short date:', error);
    return '';
  }
};

/**
 * Get relative time string (e.g., "2 days ago", "in 3 hours")
 * @param {string|Date} date - The date to format
 * @returns {string} - Relative time string
 */
export const getRelativeTime = (date) => {
  if (!date) return '';
  
  try {
    // Parse backend UTC timestamps safely (e.g., "YYYY-MM-DD HH:MM:SS") as UTC
    let dateMs;
    if (typeof date === 'string') {
      let s = date.trim();
      if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}$/.test(s)) {
        s = s.replace(' ', 'T') + 'Z';
      }
      dateMs = new Date(s).getTime();
    } else {
      dateMs = date?.getTime?.();
    }
    if (!dateMs || Number.isNaN(dateMs)) return '';

    // Relative difference is timezone-agnostic once parsed correctly as UTC
    const nowMs = Date.now();
    const diffInSeconds = Math.floor((nowMs - dateMs) / 1000);
    
    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 31536000) {
      const months = Math.floor(diffInSeconds / 2592000);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
      const years = Math.floor(diffInSeconds / 31536000);
      return `${years} year${years > 1 ? 's' : ''} ago`;
    }
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return '';
  }
};

/**
 * Check if a date is today
 * @param {string|Date} date - The date to check
 * @returns {boolean} - True if the date is today
 */
export const isToday = (date) => {
  if (!date) return false;
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const today = new Date();
    
    return dateObj.toDateString() === today.toDateString();
  } catch (error) {
    console.error('Error checking if date is today:', error);
    return false;
  }
};

/**
 * Check if a date is in the past
 * @param {string|Date} date - The date to check
 * @returns {boolean} - True if the date is in the past
 */
export const isPast = (date) => {
  if (!date) return false;
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    
    return dateObj < now;
  } catch (error) {
    console.error('Error checking if date is in the past:', error);
    return false;
  }
};

/**
 * Check if a date is in the future
 * @param {string|Date} date - The date to check
 * @returns {boolean} - True if the date is in the future
 */
export const isFuture = (date) => {
  if (!date) return false;
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    
    return dateObj > now;
  } catch (error) {
    console.error('Error checking if date is in the future:', error);
    return false;
  }
};

// Default export with all functions
export default {
  formatDate,
  formatDateTime,
  formatMonthYear,
  formatDateShort,
  getRelativeTime,
  isToday,
  isPast,
  isFuture
};
